import { useCallback } from 'react';
import {
  useSlotInChronologyEntriesMutation,
  useLazyGetChronologyQuery,
  useSlotInTimelineEntriesMutation,
  useLazyGetTimelineQuery,
} from '@/api';
import { TimelineChronologyEntry } from '@/store';

interface UseTimelineChronologySlotInProps {
  caseId: string | null;
  isChronologyActive: boolean;
  isTimelineActive: boolean;
  mixedListEntries?: (string | TimelineChronologyEntry | null | undefined)[];
  entryIdToMixedIndexMap: Map<string, number>;
}

export const useTimelineChronologySlotIn = ({
  caseId,
  isChronologyActive,
  isTimelineActive,
  mixedListEntries,
  entryIdToMixedIndexMap,
}: UseTimelineChronologySlotInProps) => {
  const [slotInChronologyEntries] = useSlotInChronologyEntriesMutation();
  const [slotInTimelineEntries] = useSlotInTimelineEntriesMutation();
  const [triggerGetChronology] = useLazyGetChronologyQuery();
  const [triggerGetTimeline] = useLazyGetTimelineQuery();

  // Function to collect all entry IDs for a specific date group using entry index
  const getEntryIdsForDateGroup = useCallback(
    (entryId: string): string[] => {
      if (!entryId || !mixedListEntries) return [];

      // Get the index of the entry from the map
      const entryIndex = entryIdToMixedIndexMap.get(entryId);
      if (entryIndex === undefined) return [];

      // Collect all entries in this date group (from start until next header or end)
      const entryIds: string[] = [];
      for (let i = entryIndex; i < mixedListEntries.length; i++) {
        const item = mixedListEntries[i];
        if (typeof item === 'string') {
          // Found next header, stop
          break;
        } else if (typeof item === 'object' && item !== null && 'id' in item) {
          entryIds.push(item.id);
        }
      }

      return entryIds;
    },
    [mixedListEntries, entryIdToMixedIndexMap],
  );

  // Handle slot into original records using entry ID to find its group
  const handleSlotIntoOriginalRecords = useCallback(
    async (entryId: string) => {
      if (!entryId || !caseId) return;

      const entryIds = getEntryIdsForDateGroup(entryId);
      if (entryIds.length === 0) return;

      try {
        if (isChronologyActive) {
          await slotInChronologyEntries({
            caseId,
            entryIds,
          }).unwrap();

          // Refresh chronology data after successful slot-in
          triggerGetChronology({ caseId });
        } else if (isTimelineActive) {
          await slotInTimelineEntries({
            caseId,
            entryIds,
          }).unwrap();

          // Refresh timeline data after successful slot-in
          triggerGetTimeline({ caseId });
        }
      } catch (error) {
        console.error('Error slotting into original records:', error);
      }
    },
    [
      caseId,
      getEntryIdsForDateGroup,
      slotInChronologyEntries,
      slotInTimelineEntries,
      isChronologyActive,
      isTimelineActive,
      triggerGetChronology,
      triggerGetTimeline,
    ],
  );

  return {
    handleSlotIntoOriginalRecords,
    getEntryIdsForDateGroup,
  };
};
